import React, { Suspense } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Toaster } from 'react-hot-toast'

// Components
import Navbar from '@components/sections/Navbar'
import Hero from '@components/sections/Hero'
import Services from '@components/sections/Services'
import About from '@components/sections/About'
import Contact from '@components/sections/Contact'
import Footer from '@components/sections/Footer'
import WhatsAppFloat from '@components/ui/WhatsAppFloat'
import LoadingSpinner from '@components/ui/LoadingSpinner'
import ScrollToTop from '@components/ui/ScrollToTop'

// Hooks
import { useScrollProgress } from '@hooks/useScrollProgress'
import { usePreloadAssets } from '@hooks/usePreloadAssets'

// Utils
import { cn } from '@utils/cn'

const App: React.FC = () => {
    const scrollProgress = useScrollProgress()
    const { isLoading, error } = usePreloadAssets()

    if (error) {
        console.error('Error loading assets:', error)
    }

    return (
        <HelmetProvider>
            <div className={cn(
                "min-h-screen bg-white text-gray-900 overflow-x-hidden",
                "selection:bg-primary-600 selection:text-white"
            )}>
                {/* Scroll Progress Bar */}
                <div
                    className="fixed top-0 left-0 h-1 bg-gradient-to-r from-primary-600 to-accent-500 z-50 transition-all duration-300"
                    style={{ width: `${scrollProgress}%` }}
                />

                {/* Navigation */}
                <Navbar />

                {/* Main Content */}
                <main className="relative">
                    <Suspense fallback={<LoadingSpinner />}>
                        {/* Hero Section with 3D */}
                        <Hero />

                        {/* Services Section */}
                        <Services />

                        {/* About Section */}
                        <About />

                        {/* Contact Section with Map */}
                        <Contact />
                    </Suspense>
                </main>

                {/* Footer */}
                <Footer />

                {/* Floating Elements */}
                <WhatsAppFloat />
                <ScrollToTop />

                {/* Toast Notifications */}
                <Toaster
                    position="top-right"
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: '#ffffff',
                            color: '#1e293b',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        },
                        success: {
                            iconTheme: {
                                primary: '#0066cc',
                                secondary: '#ffffff',
                            },
                        },
                        error: {
                            iconTheme: {
                                primary: '#dc2626',
                                secondary: '#ffffff',
                            },
                        },
                    }}
                />

                {/* Loading Overlay */}
                {isLoading && (
                    <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
                        <LoadingSpinner size="large" />
                    </div>
                )}
            </div>
        </HelmetProvider>
    )
}

export default App